import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef, useState } from "react";
import { useAppContext } from "../../store";
import { ACTION_TYPES } from "../../store/actions";
import { loadDisplaySettings, orderNewestFirst } from "../../store/helpers";
import { fetchFinishedSalePayments, fetchSaleData, } from "../../services/HomeService";
import { getTranslation } from "../../translations";
import Sale from "../../components/Sale";
import Summary from "../../components/Summary";
import Payment from "../../components/Payment";
const totalRowClassName = "flex justify-between items-baseline mb-[10px] pt-[10px] short:mb-1 short:pt-1 text-[length:var(--text-12)] max-sm:text-base max-xxs:text-[length:var(--text-85)] text-[color:var(--color-grey-900)] last:border-b last:border-dashed last:border-[color:var(--color-grey-300)] last:pb-4 short:last:pb-2";
const changeTextClassName = "font-bold text-green-700 text-[length:var(--text-15)] short:text-[length:var(--text-125)]";
const roundToCents = (amount) => Math.round(amount * 100) / 100;
const HomePage = () => {
    const { state, dispatch } = useAppContext();
    const [{ lineOrder, standbyDelay }] = useState(loadDisplaySettings);
    const lastSaleRef = useRef(undefined);
    const saleEndedAtRef = useRef(undefined);
    const oldestFirst = lineOrder === "oldestFirst";
    const loginConfig = state.loginConfig;
    const language = state.language;
    const labels = getTranslation(language);
    if (!loginConfig)
        return;
    useEffect(() => {
        const standbyDelayMs = standbyDelay * 1000;
        const loadData = async () => {
            const data = await fetchSaleData(loginConfig);
            if (!data) {
                console.warn("No data fetched.");
                return;
            }
            const saleEnded = data.saleLines.length === 0 && data.paymentLines.length === 0;
            if (!saleEnded) {
                saleEndedAtRef.current = undefined;
                lastSaleRef.current = data;
                dispatch({ type: ACTION_TYPES.SET_SALE_DATA, payload: data });
                return;
            }
            const finishedSale = lastSaleRef.current;
            if (!finishedSale)
                return;
            if (saleEndedAtRef.current === undefined) {
                saleEndedAtRef.current = Date.now();
                const paymentLines = await fetchFinishedSalePayments(loginConfig, finishedSale.receiptNo);
                if (lastSaleRef.current !== finishedSale)
                    return;
                if (paymentLines) {
                    dispatch({
                        type: ACTION_TYPES.SET_SALE_DATA,
                        payload: { ...finishedSale, paymentLines },
                    });
                    return;
                }
            }
            else if (Date.now() - saleEndedAtRef.current < standbyDelayMs) {
                return;
            }
            lastSaleRef.current = undefined;
            dispatch({ type: ACTION_TYPES.SET_SALE_DATA, payload: data });
        };
        loadData();
        const intervalId = setInterval(loadData, 1500);
        return () => clearInterval(intervalId);
    }, [dispatch, loginConfig, standbyDelay]);
    const { saleLines = [], paymentLines = [], subtotal = 0, discount = 0, tax = 0, } = state.saleData || {};
    const noData = saleLines.length === 0 &&
        paymentLines.length === 0 &&
        subtotal === 0 &&
        discount === 0 &&
        tax === 0;
    const isChangeLine = (line) => subtotal > 0 && line.amountInclVat < 0;
    const paidLines = paymentLines.filter((line) => !isChangeLine(line));
    const paidAmount = paidLines.reduce((sum, line) => sum + line.amountInclVat, 0);
    const retailChange = paymentLines
        .filter(isChangeLine)
        .reduce((sum, line) => sum - line.amountInclVat, 0);
    const due = roundToCents(subtotal - paidAmount);
    const isRefund = roundToCents(subtotal) < 0 && due <= 0;
    const remainingPayment = isRefund ? -due : Math.max(0, due);
    const change = subtotal > 0 ? retailChange || Math.max(0, -due) : 0;
    return (_jsx("div", { className: `p-8 max-sm:p-4 max-xxs:pt-8 bg-[var(--secondary-color,var(--color-secondary))] flex flex-col items-center justify-center max-w-[50rem] my-8 mx-auto short:my-0 short:p-4 rounded-[var(--radius)] shadow-[0_8px_20px_rgba(0,0,0,0.1)] ${noData ? "min-h-[37.5rem] short:min-h-[min(37.5rem,calc(100vh-1rem))]" : "h-[calc(100vh-6rem)] short:h-[calc(100vh-1rem)]"}`, children: noData ? (_jsx("div", { className: "m-auto text-center text-6xl text-[color:var(--color-grey-600)] p-8 font-[inherit]", children: labels.welcome })) : (_jsxs("div", { className: "w-full max-w-[50rem] bg-white shadow-[0_6px_16px_rgba(0,0,0,0.08)] overflow-hidden relative flex flex-col flex-1", children: [_jsx("div", { className: "w-full h-[10px] shrink-0 bg-[repeating-linear-gradient(-45deg,var(--color-white)_0_5px,var(--secondary-color,var(--color-secondary))_5px,var(--secondary-color,var(--color-secondary))_10px)]" }), _jsx("h1", { className: "text-[length:var(--text-175)] short:text-[length:var(--text-125)] font-semibold text-[color:var(--color-grey-850)] text-center uppercase pt-8 short:pt-4 pb-0 tracking-[0.03em]", children: labels.yourOrder }), _jsx(Sale, { language: language, saleLines: oldestFirst ? saleLines : orderNewestFirst(saleLines), subtotal: subtotal, oldestFirst: oldestFirst }), _jsx(Summary, { discount: discount, language: language, tax: tax }), paidLines.length > 0 && _jsx(Payment, { paymentLines: paidLines }), _jsxs("div", { className: "bg-white text-[length:var(--text-11)] text-[color:var(--color-grey-850)] flex flex-col gap-3 m-0 px-[3.3rem] py-2  max-sm:py-2 max-sm:px-6 max-sm:text-base max-sm:gap-2 short:gap-1 short:py-1", children: [_jsxs("div", { className: totalRowClassName, children: [_jsx("strong", { className: "font-semibold", children: isRefund ? labels.refundDue : labels.remainingPayment }), _jsx("strong", { className: "font-semibold", children: remainingPayment.toFixed(2) })] }), change > 0 && (_jsxs("div", { className: totalRowClassName, children: [_jsx("strong", { className: changeTextClassName, children: labels.change }), _jsx("strong", { className: changeTextClassName, children: change.toFixed(2) })] }))] }), _jsx("div", { className: "w-full h-[10px] shrink-0 rotate-180 bg-[repeating-linear-gradient(-45deg,var(--color-white)_0_5px,var(--secondary-color,var(--color-secondary))_5px,var(--secondary-color,var(--color-secondary))_10px)]" })] })) }));
};
export default HomePage;
